import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import VideoTestimonial from '@/components/home/VideoTestimonial';
import Video3DWrapper from '@/components/home/Video3DWrapper';
import FacebookReviews from '@/components/shared/FacebookReviews';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, Globe } from 'lucide-react';

const clientProjects = [
  {
    client: 'Kitter',
    project: 'E-Commerce & Branding',
    summary: 'Custom e-commerce storefront with full brand identity, designed for a modern retail experience.',
    website: 'https://codewithsadee.github.io/kitter/',
    image: '/ecommerce.png',
    technologies: ['HTML', 'CSS', 'JavaScript', 'Branding'],
  },
  {
    client: 'LearnKogit',
    project: 'Education Platform',
    summary: 'Interactive learning platform built to deliver structured tech courses with an engaging UX.',
    website: 'https://learnkogit.com/',
    image: '/kogit.png',
    technologies: ['React', 'Node.js', 'Supabase', 'Express'],
  },
  {
    client: 'Ledger Tree Financial Group',
    project: 'Financial Firm Website',
    summary: 'Fintech web presence focused on trust, compliance, and converting qualified prospects.',
    website: 'https://ledgertreefinancialgroup.com/',
    image: '/ledgertree.png',
    technologies: ['WordPress', 'Custom PHP', 'Financial APIs', 'Responsive'],
  },
  {
    client: 'Baker H Media Productions',
    project: 'Video Streaming & Stripe Payments',
    summary: 'Personal video streaming platform with Stripe integration and full payment management, letting the client deliver paid content directly to their audience.',
    website: 'https://www.bakerandhmediaproductions.com/',
    image: '/baker.png',
    technologies: ['Next.js', 'Stripe', 'Video Streaming', 'Subscriptions'],
  },
];

const instagramReels = [
  {
    permalink: 'https://www.instagram.com/reel/DSYx_vqjO2B/',
    client: 'Baker H Media Productions',
    website: 'https://www.bakerandhmediaproductions.com/',
    caption: 'Video streaming platform with Stripe payments built for Baker H Media Productions.',
  },
  {
    permalink: 'https://www.instagram.com/reel/DS1J4CoDPBd/',
    client: 'Kitter',
    website: 'https://codewithsadee.github.io/kitter/',
    caption: 'E-commerce storefront and full brand identity built for Kitter.',
  },
];

const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/omnicraft_services';
const INSTAGRAM_PROFILE_HANDLE = '@omnicraft_services';

const facebookReviews = [
  {
    name: 'Pietro Baker',
    initials: 'PB',
    avatarColor: 'bg-gradient-to-br from-emerald-500 to-teal-600',
    review:
      'OmniCraft Services built my website and provided excellent advice on how to make it more aesthetically pleasing and customer-focused. Their team is knowledgeable, professional, and incredibly informative. They took the time to ensure my site not only looked great but was designed to attract and engage more customers. I highly recommend OmniCraft Services to anyone looking for quality web design and a team that truly knows their craft.',
    reply: 'Thankyou sir !!',
  },
  {
    name: 'Michael Nwosu',
    initials: 'MN',
    avatarColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
    review:
      'This company has been amazing to work with and they have taken the time to educate me on everything they do. They are responsive, friendly and willing to take their time to provide a quality service. Our SEO and digital footprint has improved tremendously since they onboarded us. I look forward to continuing to work with them and make strides to improve our digital side of our business.',
    reply: 'Thankyou mike for your kind words.',
  },
  {
    name: 'Kogit Labs',
    initials: 'KL',
    avatarColor: 'bg-gradient-to-br from-sky-500 to-indigo-600',
    review:
      'Omnicraft Services is great for making custom websites. We would recommend it to anyone who wants to get a site online without any hassle, as-well as service that goes above and beyond!',
    reply: 'Thankyou for your kind words! 🙌',
  },
];

const socialLinks = [
  {
    name: 'Instagram',
    handle: '@omnicraft_services',
    description: 'Behind-the-scenes design work, project reels, and team updates.',
    href: 'https://www.instagram.com/omnicraft_services',
    bg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    handle: 'OmniCraft Services',
    description: 'Read more recommendations from clients we’ve worked with.',
    href: 'https://www.facebook.com/search/top?q=omnicraft%20services',
    bg: 'bg-[#1877F2]',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
];

const Testimonials = () => {
  useEffect(() => {
    const SCRIPT_SRC = 'https://www.instagram.com/embed.js';
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    if (!existing) {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    } else if ((window as any).instgrm?.Embeds?.process) {
      (window as any).instgrm.Embeds.process();
    }
  }, []);

  useEffect(() => {
    const imgObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const dataSrc = img.getAttribute('data-src');
          if (dataSrc) {
            img.src = dataSrc;
            img.removeAttribute('data-src');
          }
          imgObserver.unobserve(img);
        }
      });
    });
    const images = document.querySelectorAll('.lazy-image');
    images.forEach((img) => imgObserver.observe(img));
    return () => {
      images.forEach((img) => imgObserver.unobserve(img));
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Client Testimonials & Reviews | OmniCraft Services</title>
        <meta
          name="description"
          content="See what our clients say about OmniCraft Services. Watch video testimonials, read Facebook recommendations, and connect with us on Instagram and Facebook."
        />
        <meta name="keywords" content="omnicraft testimonials, omnicraft reviews, client video testimonials, web design reviews, custom website reviews, facebook reviews, instagram" />
        <meta property="og:title" content="Client Testimonials & Reviews | OmniCraft Services" />
        <meta
          property="og:description"
          content="Hear from the clients we've worked with — video success stories, Facebook recommendations, and our social channels."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.omnicraftservices.com/testimonials" />
        <link rel="canonical" href="https://www.omnicraftservices.com/testimonials" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'OmniCraft Services',
          url: 'https://www.omnicraftservices.com',
          sameAs: [
            'https://www.instagram.com/omnicraft_services',
            'https://www.facebook.com/search/top?q=omnicraft%20services',
          ],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '5',
            reviewCount: facebookReviews.length.toString(),
          },
          review: facebookReviews.map((r) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.name },
            reviewBody: r.review,
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          })),
        })}</script>
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#fbfaf5] text-slate-900 dark:bg-slate-950 dark:text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-28 left-[-10%] h-[34rem] w-[34rem] rounded-full bg-emerald-300/45 blur-3xl dark:bg-emerald-400/12" />
          <div className="absolute top-16 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-amber-200/40 blur-3xl dark:bg-amber-300/12" />
          <div className="absolute bottom-[-10rem] left-[14%] h-[30rem] w-[30rem] rounded-full bg-lime-200/42 blur-3xl dark:bg-lime-400/12" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.98),_rgba(255,255,255,0.78)_22%,_rgba(251,250,245,0.5)_55%,_rgba(243,249,239,0.32)_75%,_rgba(243,249,239,0)_100%)] dark:bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.7),_rgba(15,23,42,0.86)_45%,_rgba(2,6,23,1)_100%)]" />
        </div>

        <div className="relative z-10">
          <section className="px-4 pt-28 pb-10 sm:pt-32 sm:pb-14">
            <div className="mx-auto max-w-5xl text-center">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                Testimonials & Reviews
              </p>
              <h1 className="mx-auto max-w-4xl text-4xl font-semibold leading-tight text-slate-950 md:text-6xl dark:text-white">
                What Our Clients Say About Us
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-base text-slate-600 dark:text-slate-400 md:text-lg">
                Watch video success stories, read Facebook recommendations, and follow our work on social.
              </p>

              <div className="mt-8 flex items-center justify-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                ))}
                <span className="ml-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                  5.0 average from clients we’ve worked with
                </span>
              </div>
            </div>
          </section>

          <section className="px-4 pb-12 pt-4 sm:pb-20">
            <div className="mx-auto max-w-7xl">
              <Video3DWrapper>
                <VideoTestimonial excludeIds={[1]} />
              </Video3DWrapper>
            </div>
          </section>

          <section className="px-4 pb-12 pt-4 sm:pb-20">
            <div className="mx-auto max-w-5xl">
              <div className="mb-10 text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-orange-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-pink-600 dark:text-pink-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  Instagram Reels
                </div>
                <h2 className="mx-auto max-w-4xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl dark:text-white">
                  More Client Stories on Instagram
                </h2>
              </div>

              <div className="grid gap-10 md:grid-cols-2">
                {instagramReels.map(({ permalink, client, website, caption }) => (
                  <div key={permalink} className="flex flex-col items-center">
                    <div className="w-full max-w-[540px]">
                      <blockquote
                        className="instagram-media"
                        data-instgrm-captioned
                        data-instgrm-permalink={`${permalink}?utm_source=ig_embed&utm_campaign=loading`}
                        data-instgrm-version="14"
                        style={{
                          background: '#FFF',
                          border: 0,
                          borderRadius: 3,
                          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
                          margin: 1,
                          maxWidth: 540,
                          minWidth: 326,
                          padding: 0,
                          width: 'calc(100% - 2px)',
                        }}
                      >
                        <a href={`${permalink}?utm_source=ig_embed&utm_campaign=loading`} target="_blank" rel="noopener noreferrer">
                          View this reel on Instagram
                        </a>
                      </blockquote>
                    </div>
                    <p className="mt-4 max-w-xl text-center text-sm text-slate-600 dark:text-slate-400">{caption}</p>
                    <a
                      href={INSTAGRAM_PROFILE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-pink-600 hover:text-pink-700 dark:text-pink-300 dark:hover:text-pink-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                      Posted by {INSTAGRAM_PROFILE_HANDLE}
                    </a>
                    <a
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-950 px-5 py-2 text-sm font-medium text-white transition-transform duration-300 hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
                    >
                      Visit {client}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <FacebookReviews
            heading="What Clients Say on Facebook"
            subheading="Genuine recommendations straight from Facebook."
          />

          <section className="px-4 py-14 sm:py-20">
            <div className="mx-auto max-w-7xl">
              <div className="mb-12 text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">
                  <Globe className="h-3.5 w-3.5" />
                  What We Built
                </div>
                <h2 className="mx-auto max-w-4xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl dark:text-white">
                  Live Client Websites
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600 dark:text-slate-400">
                  Every video above represents a shipped project. Visit the live sites we built and branded for each client.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {clientProjects.map(({ client, project, summary, website, image, technologies }) => (
                  <Card
                    key={client}
                    className="hover-card flex h-full flex-col overflow-hidden border border-[#e7e0cf] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900/65"
                  >
                    <div className="relative h-40 overflow-hidden bg-slate-100 dark:bg-slate-800 md:h-48">
                      <img
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110 lazy-image"
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E"
                        data-src={image}
                        alt={`${client} – ${project} preview`}
                        loading="lazy"
                      />
                    </div>
                    <CardContent className="flex flex-1 flex-col p-5">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-400">
                        {project}
                      </p>
                      <h3 className="mb-2 text-lg font-semibold text-slate-950 md:text-xl dark:text-white">{client}</h3>
                      <p className="mb-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
                        {summary}
                      </p>
                      <div className="mb-5 flex flex-wrap gap-2">
                        {technologies.slice(0, 4).map((tech) => (
                          <span
                            key={tech}
                            className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      <Button
                        asChild
                        className="mt-auto w-full font-semibold"
                      >
                        <a href={website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center">
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Live Project
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 py-14 sm:py-20">
            <div className="mx-auto max-w-6xl">
              <div className="mb-12 text-center">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                  Follow Along
                </p>
                <h2 className="mx-auto max-w-4xl text-3xl font-semibold leading-tight text-slate-950 md:text-5xl dark:text-white">
                  Connect With Us on Social
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-base text-slate-600 dark:text-slate-400">
                  See our latest projects, design insights, and client wins on Instagram and Facebook.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {socialLinks.map(({ name, handle, description, href, bg, icon }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <Card className="h-full rounded-[1.75rem] border border-[#e7e0cf] bg-white/90 shadow-[0_16px_40px_rgba(15,23,42,0.08)] transition-transform duration-300 group-hover:-translate-y-1 dark:border-white/10 dark:bg-slate-900/65">
                      <CardContent className="flex items-start gap-5 p-6 sm:p-8">
                        <div className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl text-white shadow-sm ${bg}`}>
                          {icon}
                        </div>
                        <div className="flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <h3 className="text-xl font-semibold text-slate-950 dark:text-white">{name}</h3>
                            <ExternalLink className="h-4 w-4 text-slate-400 transition-colors group-hover:text-emerald-600" />
                          </div>
                          <p className="mb-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">{handle}</p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 pb-20 pt-4 sm:pb-28">
            <div className="mx-auto max-w-4xl rounded-[2.25rem] bg-slate-950 px-6 py-14 text-center text-white shadow-[0_24px_80px_rgba(15,23,42,0.24)] dark:bg-white dark:text-slate-950">
              <h2 className="text-3xl font-semibold md:text-5xl">
                Ready to Be Our Next Success Story?
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-white/85 dark:text-slate-700 md:text-xl">
                Tell us what you're trying to build. We'll tell you honestly whether we're the right team for it — and what it would take.
              </p>

              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  className="h-14 rounded-full bg-white px-8 text-base text-slate-950 shadow-lg transition-transform duration-300 hover:scale-[1.02] hover:bg-slate-100 dark:bg-slate-950 dark:text-white dark:hover:bg-slate-800"
                >
                  <Link to="/affordable-website-design">Start Your Project</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="h-14 rounded-full border-white/40 bg-transparent px-8 text-base text-white hover:bg-white/10 dark:border-slate-300 dark:text-slate-950 dark:hover:bg-slate-100"
                >
                  <Link to="/portfolio">See Our Work</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Testimonials;
